import { Grid } from '@mui/material';
import { GridColDef, DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  getCommunicationAcrossAllWorkspaces,
  ChartFrequency,
  ICommunicationAnalytics,
} from 'wisestep-api';
import { getDateUsingFormat, Item, WsColors, WsTypography, WsTypoVariants } from 'wisestep-base';

import { ICallsByStatusResponse, ICallsMadeByStatus } from '../../../composableAnalytics';

import { IDuration } from './DurationDropDown';

export interface ICommunicationsTable {
  query: ICommunicationAnalytics;
  duration?: IDuration;
}

const CommunicationsTable = ({ query, duration }: ICommunicationsTable) => {
  const paginationSize = 10;
  const tableData: Record<string, unknown>[] = [];

  const [aggregatedData, setAggregatedData] = useState<ICallsByStatusResponse>();

  useEffect(() => {
    const fetchData = async () => {
      const response = await getCommunicationAcrossAllWorkspaces(query);
      setAggregatedData({
        calls: response.data,
        statuses: response.data.distinctResults?.sort((a, b) => b.localeCompare(a)),
      });
    };
    fetchData();
  }, [query]);

  const numericColumns: GridColDef[] = [{ field: 'total', headerName: 'Total', width: 110 }];
  const nonNumericColumns: GridColDef[] = [
    { field: 'id', headerName: 'id', hide: true },
    {
      field: 'workspace',
      headerName: query.loadAllWorkspacesData ? 'Workspace' : 'Created By',
      width: 200,
    },
    { field: 'direction', headerName: 'Direction', width: 100 },
    { field: 'source', headerName: 'Source', width: 100 },
  ];

  aggregatedData?.statuses?.map((status, index) =>
    numericColumns.push({
      field: `${index}`,
      headerName:
        query?.frequency == ChartFrequency.HOURS
          ? getDateUsingFormat(status, 'HH:mm')
          : getDateUsingFormat(status, 'DD MMM'),
      width: ChartFrequency.DAILY ? 130 : 80,
    })
  );
  const getColumnValue = (item: ICallsMadeByStatus, index): number => {
    if (aggregatedData?.statuses) {
      const column =
        numericColumns[numericColumns?.length - (aggregatedData?.statuses?.length ?? 0) + index];
      for (const entry in item?.statusWithCount) {
        const formattedEntry =
          query?.frequency == ChartFrequency.HOURS
            ? getDateUsingFormat(entry, 'HH:mm')
            : getDateUsingFormat(entry, 'DD MMM');
        if (formattedEntry === column.headerName) return item?.statusWithCount?.[entry];
      }
    }
    return 0;
  };

  for (const data in aggregatedData?.calls) {
    const item: ICallsMadeByStatus = aggregatedData?.calls?.[data];
    if (item?.createdBy) {
      const row: Record<string, unknown> = {
        id: uuidv4(),
        workspace: item.createdBy,
        direction: item.direction,
        source: item.source,
        total: item.totalCalls,
      };
      aggregatedData?.statuses?.forEach((_, index) => {
        row[`${index}`] = getColumnValue(item, index);
      });
      tableData.push(row);
    }
  }

  const getColumnSum = (column) =>
    tableData.reduce((total, row) => total + Number(row[column.field]), 0);

  const columnsWithSum = numericColumns.map((column) => ({
    ...column,
    headerName: `${column.headerName}  (${getColumnSum(column)})`,
  }));

  return (
    <Grid item xs={12} bgcolor={WsColors.White}>
      <Item>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold={true}>
          {`SMS Data in ${duration ?? 'last 60 Days - Weekly'}`}
        </WsTypography>
      </Item>
      <Item>
        <DataGrid
          columns={[...nonNumericColumns, ...columnsWithSum]}
          rows={tableData}
          components={{ Toolbar: GridToolbar }}
          pageSize={paginationSize}
          rowsPerPageOptions={[paginationSize]}
          autoHeight={true}
        />
      </Item>
    </Grid>
  );
};
export default CommunicationsTable;
